"use client";

import { useSearchParams } from "next/navigation";
import { useState, useEffect } from "react";
import { ProductGrid } from "@/components/ProductGrid";
import { useProducts } from "@/hooks/useProducts";
import { ProductFilters } from "@/types/woocommerce";
import { Navbar, NavbarBrand, NavbarContent, Button } from "@nextui-org/react";
import { ShoppingBag, ArrowLeft } from "lucide-react";
import Link from "next/link";

export default function ProductsResultsPage() {
  const searchParams = useSearchParams();
  const [filters, setFilters] = useState<ProductFilters>({});

  useEffect(() => {
    // Parse query parameters into filters
    const parsedFilters: ProductFilters = {};

    // Parse excluded categories
    const excluded = searchParams.get('excluded');
    if (excluded) {
      parsedFilters.excludedCategories = excluded.split(',').map(id => parseInt(id));
    }

    // Parse basic filters
    const search = searchParams.get('search');
    if (search) parsedFilters.search = search;

    const category = searchParams.get('category');
    if (category) parsedFilters.category = category;

    const minPrice = searchParams.get('minPrice');
    if (minPrice) parsedFilters.minPrice = parseFloat(minPrice);

    const maxPrice = searchParams.get('maxPrice');
    if (maxPrice) parsedFilters.maxPrice = parseFloat(maxPrice);

    const onSale = searchParams.get('onSale');
    if (onSale === 'true') parsedFilters.onSale = true;

    const inStock = searchParams.get('inStock');
    if (inStock === 'true') parsedFilters.inStock = true;

    const orderby = searchParams.get('orderby');
    if (orderby) parsedFilters.orderby = orderby as any;

    // Parse attribute filters
    const gorivo = searchParams.get('gorivo');
    if (gorivo) parsedFilters.gorivo = gorivo.split(',');

    const frekvencija = searchParams.get('frekvencija');
    if (frekvencija) parsedFilters.frekvencija = frekvencija.split(',');

    const faza = searchParams.get('faza');
    if (faza) parsedFilters.faza = faza.split(',');

    const emisija = searchParams.get('emisija');
    if (emisija) parsedFilters.emisija = emisija.split(',');

    const minSnaga = searchParams.get('minSnaga');
    if (minSnaga) parsedFilters.minSnaga = parseFloat(minSnaga);

    const maxSnaga = searchParams.get('maxSnaga');
    if (maxSnaga) parsedFilters.maxSnaga = parseFloat(maxSnaga);

    setFilters(parsedFilters);
  }, [searchParams]);

  const { products, loading, error, initialLoad } = useProducts(filters);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <Navbar position="sticky" className="bg-white shadow-sm">
        <NavbarBrand>
          <ShoppingBag className="w-6 h-6 text-primary mr-2" />
          <p className="font-bold text-xl">Product Results</p>
        </NavbarBrand>
        <NavbarContent justify="end">
          <Button
            as={Link}
            href="/products"
            variant="flat"
            startContent={<ArrowLeft className="w-4 h-4" />}
          >
            Back to Filters
          </Button>
        </NavbarContent>
      </Navbar>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Search Results
          </h1>
          <p className="text-gray-600">
            Products matching your selected criteria
          </p>
        </div>

        {/* Products Grid */}
        <ProductGrid 
          products={products} 
          loading={loading} 
          error={error} 
          initialLoad={initialLoad}
        />
      </main>
    </div>
  );
}
